import { c as _c } from "react/compiler-runtime";
import { Popover, Tooltip as SioTooltip } from '@systemeio/ui';
import { BarElement, CategoryScale, ChartData, Chart as ChartJS, Legend, LinearScale, ScatterDataPoint, Title, Tooltip } from 'chart.js';
import { EmailStatisticsChartAmountEnum, EmailStatisticsChartPercentEnum, emailStatisticsChartTitles } from 'modules/email-statistics/enums/email-statistics-chart-enum';
import { useEmailStatisticsCharts } from 'modules/email-statistics/hooks/use-email-statistics-charts';
import { EmailStatisticsChartDataType, EmailStatisticsFilterInterface } from 'modules/email-statistics/types/email-statistics-interface';
import { getEmailStatisticsChartOptions } from 'modules/email-statistics/utils/get-email-statistics-chart-options';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { TLocoType, useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { toShortDate } from 'shared/utils/date-time-format';
import { QuestionMarkIcon } from '@systemeio/ui/icons';
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value';
interface EmailStatisticsChartsProps {
  filter: EmailStatisticsFilterInterface;
}
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const EmailStatisticsCharts = t0 => {
  const $ = _c(25);
  const {
    filter
  } = t0;
  const {
    data: chartsData
  } = useEmailStatisticsCharts(filter);
  const t1 = !chartsData;
  const t2 = chartsData?.totalSent;
  const t3 = chartsData?.totalSent !== 0;
  const t4 = chartsData?.byDayStats;
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = <EmailStatisticsChartItem isPreFetching={t1} type={EmailStatisticsChartAmountEnum.sentAmount} total={t2} showGrid={t3} chartStats={t4} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = !chartsData;
  const t7 = chartsData?.averageStats.openedPercent;
  const t8 = chartsData?.averageStats.openedPercent !== 0;
  const t9 = chartsData?.byDayStats;
  let t10;
  if ($[5] !== t6 || $[6] !== t7 || $[7] !== t8 || $[8] !== t9) {
    t10 = <EmailStatisticsChartItem isPreFetching={t6} type={EmailStatisticsChartPercentEnum.openedPercent} total={t7} showGrid={t8} chartStats={t9} />;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = !chartsData;
  const t12 = chartsData?.averageStats.spamReportsPercent;
  const t13 = chartsData?.averageStats.spamReportsPercent !== 0;
  const t14 = chartsData?.byDayStats;
  let t15;
  if ($[10] !== t11 || $[11] !== t12 || $[12] !== t13 || $[13] !== t14) {
    t15 = <EmailStatisticsChartItem isPreFetching={t11} type={EmailStatisticsChartPercentEnum.spamReportsPercent} total={t12} showGrid={t13} chartStats={t14} />;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  const t16 = !chartsData;
  const t17 = chartsData?.averageStats.bouncedPercent;
  const t18 = chartsData?.averageStats.bouncedPercent !== 0;
  const t19 = chartsData?.byDayStats;
  let t20;
  if ($[15] !== t16 || $[16] !== t17 || $[17] !== t18 || $[18] !== t19) {
    t20 = <EmailStatisticsChartItem isPreFetching={t16} type={EmailStatisticsChartPercentEnum.bouncedPercent} total={t17} showGrid={t18} chartStats={t19} />;
    $[15] = t16;
    $[16] = t17;
    $[17] = t18;
    $[18] = t19;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  let t21;
  if ($[20] !== t10 || $[21] !== t15 || $[22] !== t20 || $[23] !== t5) {
    t21 = <div className="flex flex-wrap gap-5">{t5}{t10}{t15}{t20}</div>;
    $[20] = t10;
    $[21] = t15;
    $[22] = t20;
    $[23] = t5;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  return t21;
};
interface EmailStatisticsChartItemProps {
  isPreFetching: boolean;
  type: EmailStatisticsChartAmountEnum | EmailStatisticsChartPercentEnum;
  total?: number;
  showGrid: boolean;
  chartStats?: Record<string, EmailStatisticsChartDataType>;
}
const EmailStatisticsChartItem = t0 => {
  const $ = _c(20);
  const {
    type,
    showGrid,
    total,
    isPreFetching,
    chartStats
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    intlLocale
  } = useUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      labels: [],
      datasets: []
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [data, setData] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== chartStats || $[2] !== intlLocale || $[3] !== type) {
    t2 = () => {
      if (chartStats) {
        setData({
          labels: Object.keys(chartStats).map(el => toShortDate(el, intlLocale)),
          datasets: [{
            data: Object.values(chartStats).map(el_0 => el_0[type]),
            backgroundColor: getCssRgbValue("--brand-primary"),
            borderRadius: 2
          }]
        });
      }
    };
    t3 = [chartStats, type, intlLocale];
    $[1] = chartStats;
    $[2] = intlLocale;
    $[3] = type;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== isPreFetching || $[7] !== t || $[8] !== total || $[9] !== type) {
    t4 = getChartTitle(t, type, isPreFetching, total);
    $[6] = isPreFetching;
    $[7] = t;
    $[8] = total;
    $[9] = type;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <div className="text-text-primary bg-brand-primary-subtle flex items-center gap-1 px-6 py-4 text-[17px] font-bold">{t4}</div>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== data || $[14] !== isPreFetching || $[15] !== showGrid) {
    t6 = <div className="px-6 py-4">{!isPreFetching ? <Bar options={getEmailStatisticsChartOptions(showGrid)} data={data} height={430} /> : <div className="bg-skeleton-primary h-[430px] animate-pulse rounded-lg text-transparent ">.</div>}</div>;
    $[13] = data;
    $[14] = isPreFetching;
    $[15] = showGrid;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className="flex w-full flex-col gap-5 lg:w-[calc(50%-10px)] lg:flex-row"><div className="bg-background-secondary flex h-fit w-full flex-col justify-between overflow-x-hidden rounded-lg">{t5}{t6}</div></div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
const getChartTitle = (t: TLocoType, type: EmailStatisticsChartItemProps['type'], isPreFetching: boolean, total?: number) => {
  switch (type) {
    case EmailStatisticsChartAmountEnum.sentAmount:
      {
        return <>
          <SioTooltip wrapperClassName={'truncate'} label={t(emailStatisticsChartTitles[type])} mode={'overflow'}>{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? <span className={'text-brand-primary'}>{total}</span> : <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>}
        </>;
      }
    case EmailStatisticsChartPercentEnum.spamReportsPercent:
      {
        return <div className={'flex w-full items-center gap-1.5'}>
          <SioTooltip wrapperClassName={'truncate'} label={t(emailStatisticsChartTitles[type])} mode={'overflow'}>{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? <span className={'text-brand-primary'}>{`${total.toFixed(1)}%`}</span> : <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>}
          <Popover label={t('dashboard.email_statistics.spam_percent_description')} popoverClassName={'max-w-[200px] sm:max-w-[350px]'}>
            {show => <QuestionMarkIcon className={`${show ? `fill-brand-primary` : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
          </Popover>
        </div>;
      }
    default:
      {
        return <>
          <SioTooltip wrapperClassName={'truncate'} label={t(emailStatisticsChartTitles[type])} mode={'overflow'}>{`${t(emailStatisticsChartTitles[type])}:`}</SioTooltip>
          {!isPreFetching && total !== undefined ? <span className={'text-brand-primary'}>{`${total.toFixed(1)}%`}</span> : <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>
              .
            </span>}
        </>;
      }
  }
};
export default EmailStatisticsCharts;